import { useState } from "react";
import { Upload, X, Loader2, Film, Image as ImageIcon } from "lucide-react";
import { api, resolveMediaUrl } from "../lib/api";
import { toast } from "sonner";

/**
 * Generic media uploader.
 * mode = "image" | "video"
 * Stores RELATIVE URLs in `items`; resolves them with REACT_APP_BACKEND_URL at render time.
 */
export default function MediaUploader({ items = [], onChange, mode = "image" }) {
  const [uploading, setUploading] = useState(false);
  const accept = mode === "video" ? "video/*" : "image/*";
  const Label = mode === "video" ? "Upload video" : "Upload";
  const Icon = mode === "video" ? Film : Upload;

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const newUrls = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const res = await api.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        // Store the relative URL exactly as backend returns.
        newUrls.push(res.data.url);
      } catch (e) {
        toast.error(`Upload failed for ${file.name}`);
      }
    }
    if (newUrls.length) {
      onChange([...(items.filter(Boolean)), ...newUrls]);
      toast.success(`${newUrls.length} ${mode}${newUrls.length === 1 ? "" : "s"} uploaded`);
    }
    setUploading(false);
  };

  const removeAt = (i) => onChange(items.filter((_, idx) => idx !== i));

  const updateUrl = (i, url) => {
    const next = [...items];
    next[i] = url;
    onChange(next);
  };

  return (
    <div data-testid={`${mode}-uploader`}>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2">
        {items.filter(Boolean).map((src, i) => (
          <div key={i} className="relative group aspect-square bg-[#141414] border border-[#262626]">
            {mode === "video" ? (
              <video src={resolveMediaUrl(src)} muted loop playsInline className="w-full h-full object-cover" />
            ) : (
              <img src={resolveMediaUrl(src)} alt="" className="w-full h-full object-cover" />
            )}
            <button
              type="button"
              onClick={() => removeAt(i)}
              data-testid={`remove-${mode}-${i}`}
              className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 backdrop-blur text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#D4AF37] hover:text-[#0A0A0A]"
            >
              <X size={14} strokeWidth={1.5} />
            </button>
          </div>
        ))}

        <label
          className={`aspect-square border border-dashed border-[#262626] hover:border-[#D4AF37] cursor-pointer flex flex-col items-center justify-center gap-2 text-[#A3A3A3] hover:text-[#D4AF37] transition-colors ${
            uploading ? "pointer-events-none opacity-60" : ""
          }`}
          data-testid={`upload-${mode}-trigger`}
        >
          {uploading ? (
            <Loader2 size={20} className="animate-spin" />
          ) : (
            <>
              <Icon size={20} strokeWidth={1.25} />
              <span className="text-[9px] tracking-[0.3em] uppercase">{Label}</span>
            </>
          )}
          <input
            type="file"
            accept={accept}
            multiple={mode === "image"}
            className="hidden"
            data-testid={`upload-${mode}-input`}
            onChange={(e) => handleFiles(e.target.files)}
          />
        </label>
      </div>

      <details className="mt-4 text-xs">
        <summary className="cursor-pointer text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] hover:text-[#D4AF37]">
          Or paste {mode} URL
        </summary>
        <div className="mt-3 space-y-2">
          {items.map((src, i) => (
            <input
              key={i}
              value={src}
              onChange={(e) => updateUrl(i, e.target.value)}
              placeholder="https://…"
              className="w-full bg-transparent border-b border-[#262626] py-2 text-sm focus:border-[#D4AF37] outline-none"
            />
          ))}
          <button
            type="button"
            onClick={() => onChange([...items, ""])}
            className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]"
          >
            + Add URL row
          </button>
        </div>
      </details>
    </div>
  );
}
